import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { fieldDisplayName, isRequiredField } from '../fillLayout';
import RequiredMark from './RequiredMark';
import FormStatusContext from './FormStatusContext';

type Props = {
  item: any;
  keyName: string;
  tab: string;
  data: Record<string, any>;
  setData: (d: Record<string, any>) => void;
  onUpperBlur: Function;
  registerRef?: (key: string, el: HTMLElement | null) => void;
  onFocusField?: (key: string) => void;
};

const MatrixTextboxes = ({
  item,
  keyName,
  tab,
  data,
  setData,
  onUpperBlur,
  registerRef = undefined,
  onFocusField = undefined,
}: Props) => {
  const [context, setContext] = useState(data);
  const statusName = useContext(FormStatusContext);

  useEffect(() => {
    setContext(data);
  }, [data]);

  const getMatrixValue = (matrix, value, row, col) => {
    return matrix.map((rowArr, rowIndex) => {
      if (Number(row) === rowIndex) {
        return rowArr.map((singleItem, colIndex) => {
          if (Number(col) === colIndex) {
            return { ...singleItem, text: value };
          }
          return singleItem;
        });
      }
      return rowArr;
    });
  };

  const onChange = e => {
    const { name, value } = e.target;
    const arr = name.split('_');
    const row = arr[1];
    const col = arr[2];
    const { matrix } = context[tab][keyName];
    const _data = {
      ...context,
      [tab]: {
        ...context[tab],
        [keyName]: {
          ...context[tab][keyName],
          matrix: getMatrixValue(matrix, value, row, col),
        },
      },
    };
    setContext(_data);
  };

  const onBlur = () => {
    setData(context);
    onUpperBlur(context);
  };

  const matrix = context[tab][keyName].matrix || [];
  const columnCount = matrix.length ? matrix[0].length : 0;
  const filled = matrix.reduce(
    (acc, row) => acc + row.filter(cell => `${cell.text ?? ''}`.trim() !== '').length,
    0,
  );

  return (
    <Wrapper>
      <Head>
        <Title>
          {fieldDisplayName(keyName, item)}
          {isRequiredField(keyName, item, statusName) && <RequiredMark />}
        </Title>
        <Meta>
          {matrix.length} × {columnCount} · {filled} filled
        </Meta>
      </Head>

      {/* A real table with row numbers and a sticky index column. The grid is
          fixed by the printed form, so rows cannot be added or removed — but at
          this size (up to 20 × 9 = 180 cells) it has to be navigable. */}
      <Scroll>
        <Table>
          <thead>
            <tr>
              <CornerCell scope="col">#</CornerCell>
              {Array.from({ length: columnCount }, (unused, col) => (
                <HeaderCell key={col} scope="col">
                  {col + 1}
                </HeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <IndexCell scope="row">{rowIndex + 1}</IndexCell>
                {row.map((cell, colIndex) => (
                  <BodyCell key={`${rowIndex}_${colIndex}`}>
                    <Cell
                      ref={el =>
                        registerRef && rowIndex === 0 && colIndex === 0 && registerRef(keyName, el)}
                      type="text"
                      aria-label={`Row ${rowIndex + 1} column ${colIndex + 1}`}
                      name={`value_${rowIndex}_${colIndex}`}
                      value={cell.text || ''}
                      onChange={onChange}
                      onFocus={() => onFocusField && onFocusField(keyName)}
                      onBlur={onBlur}
                    />
                  </BodyCell>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Scroll>
    </Wrapper>
  );
};

export default MatrixTextboxes;

const Wrapper = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Meta = styled.span`
  font-size: 0.6875rem;
  color: var(--neutral-500);
  white-space: nowrap;
`;

const Scroll = styled.div`
  max-height: 340px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
`;

const headerCellStyles = `
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--neutral-100);
  border-bottom: 1px solid var(--border);
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--neutral-500);
  padding: 6px 8px;
  text-align: center;
`;

const HeaderCell = styled.th`
  ${headerCellStyles}
`;

const CornerCell = styled.th`
  ${headerCellStyles}
  left: 0;
  z-index: 3;
  border-right: 1px solid var(--border);
  min-width: 34px;
`;

const IndexCell = styled.th`
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--neutral-50);
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--neutral-500);
  padding: 0 8px;
  text-align: center;
  min-width: 34px;
`;

const BodyCell = styled.td`
  border-bottom: 1px solid var(--border);
  padding: 0;
`;

const Cell = styled.input`
  width: 100%;
  min-width: 78px;
  height: 32px;
  padding: 0 8px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;

  &:focus {
    outline: none;
    background: var(--primary-50);
    box-shadow: inset 0 0 0 1px var(--primary-600);
  }
`;
